import { Autocomplete, Box, Button, Grid, TextField } from "@mui/material";
import { DatePanel } from "../global/js/DatePanel";
import { useEffect, useState } from "react";
import { addDays, datesBetween, endOfMonth, fmtKey, isSelectedDateIsPast, parseDateOnly } from "../../constants/DateUtils";
import { BookingTimeRangeType, isEmpty, printDebugLog } from "../../constants/Constants";
import useAuth from "../../hooks/useAuth";
import { CHECK_IN_TIME, CHECK_OUT_TIME } from "../../constants/pojoservice"
import { showAvailableRooms } from "../createBanquetBill/networkController";

const AdvanceBooking = ({
    bookingCount, previousDateBooking = [], setSnpComponent,
    navigate,
    clearSnpComponent }) => {

    const { activeShopHash, authUser } = useAuth();
    const [viewDate, setViewDate] = useState(new Date());

    const [checkInDate, setCheckInDate] = useState("");
    const [checkOutDate, setCheckOutDate] = useState("");
    const [checkInOutFocus, setCheckInOutFocus] = useState(CHECK_IN_TIME);

    const [currentDateSlot, setCurrentDateSlot] = useState(new Map());
    const [dateLoader, setDateLoader] = useState(false);
    const [roomData, setRoomData] = useState([]);
    const [selRoom, setSelRoom] = useState(null);

    function getCheckInInfo(withPrefix = true) {
        const parseCheckInDate = checkInDate ?? "";
        return `${withPrefix ? "Check In : " : ""}${parseCheckInDate}`;
    }

    function getCheckOutInfo(withPrefix = true) {
        const parseCheckDate = checkOutDate ?? "";
        return `${withPrefix ? "Check Out : " : ""}${parseCheckDate}`;
    }

    const fetchAvailableRooms = async () => {
        await showAvailableRooms({
            authUser: authUser,
            activeShopHash: activeShopHash,
            checkInDate: checkInDate,
            checkOutDate: checkOutDate,
            advanceBooking: true,
            setLoader: setDateLoader,
            onResponseCallback: (payLoad, err) => {
                if (err) {
                    setRoomData([]);
                    setSnpComponent(prev => ({
                        ...prev,
                        negativeReq: true,
                        negativeAction: "Dismiss",
                        positiveReq: false,
                        showDialog: true,
                        outSideClickDismiss: true,
                        cancelExtra: true,
                        subTitle: `No Room available from ${checkInDate} to ${checkOutDate}`,
                        handleSnpCancel: () => clearSnpComponent(),
                    }));
                } else {
                    setRoomData(payLoad?.data ?? []);
                }
            }
        })
    }

    const handleClickFull = (d) => {
        const dateStr = fmtKey(d);
        if (checkInOutFocus === CHECK_IN_TIME) {
            setCheckInOutFocus(CHECK_OUT_TIME);
            setCheckInDate(dateStr);
            setCheckOutDate("");
            dateAddOperation(dateStr, BookingTimeRangeType.FULL, currentDateSlot.size > 0);
        } else {
            const parseCheckInDate = parseDateOnly(checkInDate);
            if (isSelectedDateIsPast(d, parseCheckInDate)) {
                setSnpComponent(prev => ({
                    ...prev,
                    negativeReq: true,
                    negativeAction: "Dismiss",
                    positiveReq: false,
                    showDialog: true,
                    outSideClickDismiss: true,
                    cancelExtra: true,
                    subTitle: `Please select date greater than Check in ${checkInDate}`,
                    handleSnpCancel: () => clearSnpComponent(),
                }));
                return;
            }
            setCheckInOutFocus(CHECK_IN_TIME);
            setCheckOutDate(dateStr);
            const dateArr = datesBetween(parseCheckInDate, d);
            setCurrentDateSlot(prev => {
                const newMap = new Map(prev);
                for (let i = 0; i < dateArr.length; i++) {
                    const date = dateArr[i];
                    const strDate = fmtKey(date);
                    newMap.set(strDate, BookingTimeRangeType.FULL);
                }

                return newMap;
            });
        }
    }

    const handleFullClear = () => {
        setCheckInDate("");
        setCheckOutDate("");
        setCurrentDateSlot(new Map());
        setRoomData([]);
        setSelRoom(null);
    }

    function dateAddOperation(dateStr, timeRange, noprevData = false) {
        setCurrentDateSlot(prev => {
            const newMap = noprevData ? new Map() : new Map(prev);
            newMap.set(dateStr, timeRange);
            return newMap;
        });
    }

    useEffect(() => {
        printDebugLog("AdvanceBooking roomData", roomData);
    }, [roomData])

    return (
        <Grid container spacing={2}>
            <DatePanel
                viewDate={viewDate}
                bookingCount={bookingCount}
                readOnlyPast={true}
                disabledDate={() => { }}
                disabledHour={true}
                previousDateBooking={previousDateBooking}
                currentDateSlot={currentDateSlot}
                isDay={true}
                onClickFull={(d) => handleClickFull(d)}
                onClickPartial={() => { }}
                onClickCheckBooking={() => { }}
                goNext={() => setViewDate((d) => addDays(endOfMonth(d), 1))}
                goPrev={() => setViewDate((d) => addDays(new Date(d.setDate(1)), -1))}
                loader={dateLoader}
            />
            <Grid item xs={12} md={6} lg={6} sm={12}>
                <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                        <p style={{
                            fontWeight: "bold",
                            whiteSpace: 'pre-wrap', overflowWrap: 'break-word',
                            fontSize: '14px', justifyContent: 'center', alignItems: 'center', alignContent: 'center',
                            padding: '2px'
                        }}>{getCheckInInfo()}</p>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <p style={{
                            fontWeight: "bold",
                            whiteSpace: 'pre-wrap', overflowWrap: 'break-word',
                            fontSize: '14px', justifyContent: 'center', alignItems: 'center', alignContent: 'center',
                            padding: '2px'
                        }}>{getCheckOutInfo()}</p>
                    </Grid>
                    {
                        (!isEmpty(checkInDate) && !isEmpty(checkOutDate)) &&
                        <Grid item xs={12} md={6}>
                            <Button size="small"
                                variant="contained" onPointerDown={() => handleFullClear()}>
                                Clear
                            </Button>
                        </Grid>
                    }
                    {
                        (!isEmpty(checkInDate) && !isEmpty(checkOutDate)) &&
                        <Grid item xs={12} md={6}>
                            <Button size="small"
                                variant="contained" onPointerDown={() => fetchAvailableRooms()}>
                                Show Available Rooms
                            </Button>
                        </Grid>
                    }
                    {
                        (roomData && roomData.length > 0) &&
                        <Grid item xs={12}>
                            <Autocomplete
                                id="room-select"
                                options={roomData}
                                autoHighlight
                                variant="filled"
                                size="small"
                                fullWidth
                                onChange={(e, option) => {
                                    setSelRoom(option);
                                }}
                                isOptionEqualToValue={(option, value) =>
                                    !!value && option.id === value.id
                                }
                                getOptionLabel={(option) => option?.name ?? ""}
                                value={selRoom}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        {...props}
                                    >
                                        {option?.name ?? ""}
                                    </Box>
                                )}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Select available Room"
                                        inputProps={{
                                            ...params.inputProps,
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    }

                    {
                        selRoom &&
                        <Grid item xs={12} md={6}>
                            <Button size="small"
                                variant="contained" onPointerDown={() => navigate(selRoom.id, checkInDate, checkOutDate)}>
                                Initiate Booking
                            </Button>
                        </Grid>
                    }
                </Grid>
            </Grid>

        </Grid>
    );
};

export { AdvanceBooking };